import { Amplify } from 'aws-amplify'
import { signOut } from 'aws-amplify/auth'
import { generateClient } from 'aws-amplify/data'
import type { TenantConfig } from './tenantConfigService'

class AmplifyConfigManager {
  private static instance: AmplifyConfigManager
  private currentTenantId: string | null = null
  private isConfigured = false
  private isConfiguring = false
  private currentClient: any = null
  private currentConfig: any = null

  /**
   * Get singleton instance
   */
  static getInstance(): AmplifyConfigManager {
    if (!AmplifyConfigManager.instance) {
      AmplifyConfigManager.instance = new AmplifyConfigManager()
    }
    return AmplifyConfigManager.instance
  }

  /**
   * Configure Amplify with tenant-specific configuration
   */
  async configureTenant(config: TenantConfig): Promise<void> {
    const newTenantId = config.tenantId

    // Prevent concurrent configuration attempts
    if (this.isConfiguring) {
      console.warn('Amplify configuration already in progress')
      return
    }

    // Skip reconfiguration if it's the same tenant
    if (this.currentTenantId === newTenantId && this.isConfigured) {
      // Ensure we have a client if data config exists
      if (config.data && !this.currentClient) {
        this.currentClient = generateClient({ authMode: 'userPool' })
      }
      return
    }

    this.isConfiguring = true

    try {
      // If switching tenants, sign out first to clear session
      if (this.currentTenantId && this.currentTenantId !== newTenantId && this.isConfigured) {
        try {
          await signOut({ global: true })
        } catch (error) {
          console.warn('Error during sign out:', error)
        }
      }

      // Use the raw configuration directly - it's already in the correct amplify_outputs.json format
      // Include version field which is required by Amplify UI components
      // The config.data already includes merged model_introspection from TenantConfigService
      const amplifyConfig = { ...config }
      
      // Transform OAuth property names from old format to amplify_outputs.json expected format
      // S3 config has redirect_sign_in but parser expects redirect_sign_in_uri (as array).
      // The Hosted-UI domain is the tenant's own, published per-tenant in config.json by
      // build_tenant_config (facility.userPoolDomain) — no generic/shared fallback.
      if (amplifyConfig.auth?.oauth) {
        const oauth = amplifyConfig.auth.oauth as any
        const redirectSignIn = oauth.redirect_sign_in_uri || oauth.redirect_sign_in
        const redirectSignOut = oauth.redirect_sign_out_uri || oauth.redirect_sign_out
        
        amplifyConfig.auth.oauth = {
          domain: oauth.domain,
          // Match Gen1's requested scopes and the per-tenant app client's AllowedOAuthScopes
          // (email/openid/phone/profile). NOT aws.cognito.signin.user.admin — the client
          // never allows it, so requesting it makes Cognito reject the whole set (invalid_scope).
          scopes: ['openid', 'email', 'phone', 'profile'],
          // Ensure redirects are arrays - split by comma if string, or wrap in array
          redirect_sign_in_uri: Array.isArray(redirectSignIn) 
            ? redirectSignIn 
            : redirectSignIn?.split(',').map((url: string) => url.trim()) || [],
          redirect_sign_out_uri: Array.isArray(redirectSignOut)
            ? redirectSignOut
            : redirectSignOut?.split(',').map((url: string) => url.trim()) || [],
          response_type: oauth.response_type,
          identity_providers: oauth.identity_providers
        }
      }
      
      // If no Hosted-UI domain resolved (stale pre-publish config, or a non-SSO tenant
      // with none published), drop the oauth block so Amplify never receives a domainless
      // config. Password (SRP) login is unaffected — it doesn't use oauth.
      if (amplifyConfig.auth?.oauth && !(amplifyConfig.auth.oauth as { domain?: string }).domain) {
        console.warn('No OAuth domain configured for tenant; Hosted-UI/SSO disabled for this tenant')
        delete (amplifyConfig.auth as { oauth?: unknown }).oauth
      }

      Amplify.configure(amplifyConfig)
      
      // Store current configuration
      this.currentConfig = amplifyConfig
      
      // Generate new client for data operations
      if (config.data) {
        this.currentClient = generateClient({ authMode: 'userPool' })
      }
      
      this.currentTenantId = newTenantId
      this.isConfigured = true
      
      // Store current tenant in localStorage for persistence
      localStorage.setItem('mcr_current_tenant', newTenantId)
      
    } catch (error) {
      console.error('Failed to configure Amplify:', error)
      throw new Error(`Failed to configure Amplify for tenant ${newTenantId}: ${error}`)
    } finally {
      this.isConfiguring = false
    }
  }


  /**
   * Get current GraphQL client
   */
  getCurrentClient() {
    return this.currentClient
  }

  /**
   * Regenerate client after authentication state changes
   */
  regenerateClient(): void {
    if (this.isConfigured) {
      this.currentClient = generateClient({ authMode: 'userPool' })
    }
  }

  /**
   * Get current tenant ID
   */
  getCurrentTenantId(): string | null {
    return this.currentTenantId
  }

  /**
   * Check if Amplify is configured
   */
  isAmplifyConfigured(): boolean {
    return this.isConfigured
  }

  /**
   * Check if configuration is in progress
   */
  isCurrentlyConfiguring(): boolean {
    return this.isConfiguring
  }

  /**
   * Get current Amplify configuration
   */
  getCurrentConfig(): any {
    return this.currentConfig
  }

  /**
   * Get stored tenant ID from localStorage
   */
  getStoredTenantId(): string | null {
    try {
      return localStorage.getItem('mcr_current_tenant')
    } catch {
      return null
    }
  }

  /**
   * Clear stored tenant information
   */
  clearStoredTenant(): void {
    try {
      localStorage.removeItem('mcr_current_tenant')
    } catch (error) {
      console.warn('Failed to clear stored tenant:', error)
    }
  }

  /**
   * Mark Amplify as pre-configured (called from main.ts)
   */
  markAsPreConfigured(tenantId: string): void {
    this.currentTenantId = tenantId
    this.isConfigured = true
  }

  /**
   * Reset configuration
   */
  async reset(): Promise<void> {
    if (this.isConfigured) {
      try {
        await signOut({ global: true })
      } catch (error) {
        console.warn('Error during reset sign out:', error)
      }
    }
    
    this.currentTenantId = null
    this.isConfigured = false
    this.currentClient = null
    this.clearStoredTenant()
  }
}

export const amplifyConfigManager = AmplifyConfigManager.getInstance()
